import { Clipboard } from '@angular/cdk/clipboard'
import { booleanAttribute, Component, Input, OnInit } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { SeFeToastService } from 'se-fe-toast'
import { SeFeTranslationsLoader } from 'se-fe-translations'

@Component({
  selector: 'se-open-registration-card',
  templateUrl: './registration-card.component.html',
  styleUrls: ['./registration-card.component.scss'],
  standalone: false,
})
export class RegistrationCardComponent implements OnInit {
  private readonly translateKey = 'SE_PO_SE_REGISTRATION_UI_REGISTRATION_CARD.'
  private readonly translationsPath = 'se-registration/ui-registration-card/assets/translations'
  private readonly copyLinkErrorFallback = 'Unable to copy link'
  private readonly copyLinkSuccessFallback = 'Link copied to clipboard'

  @Input() public orgName = ''
  @Input() public orgLogoUrl = ''
  @Input() public orgColor = ''
  @Input() public registrationName = ''
  @Input() public sport = ''
  @Input() public type = ''
  @Input() public avatarUrl = ''
  @Input() public backgroundImgUrl = ''
  @Input() public infoHeaderText = ''
  @Input() public infoText = ''
  @Input() public linkUrl = ''
  @Input({ transform: booleanAttribute }) public showShareAction = true
  @Input() public buttonText = ''
  @Input() public linkText = ''
  @Input() public orderUrl = ''
  @Input() public orderRouterLink: string | unknown[] = ''
  @Input() public orderRouterQueryParams: Record<string, string | number> = {}

  public get hasOrderRouterLink(): boolean {
    const orderRouterLink = this.orderRouterLink

    if (Array.isArray(orderRouterLink)) {
      return orderRouterLink.length > 0
    }

    return typeof orderRouterLink === 'string' && orderRouterLink.trim().length > 0
  }

  constructor(
    private clipboard: Clipboard,
    private seToast: SeFeToastService,
    private translateService: TranslateService,
    private translationsLoader: SeFeTranslationsLoader
  ) {}

  public async ngOnInit(): Promise<void> {
    await this.translationsLoader.load(this.translationsPath)
  }

  public get backgroundImage(): string {
    if (!this.backgroundImgUrl && !this.orgColor) {
      return ''
    }

    const overlay = this.orgColor
      ? `linear-gradient(90deg, rgba(30, 13, 13, .82), ${this.orgColor})`
      : 'linear-gradient(90deg, rgba(30, 13, 13, .82), rgba(166, 12, 16, .84))'

    return this.backgroundImgUrl ? `${overlay}, url("${this.backgroundImgUrl}")` : overlay
  }

  public get shareLabel(): string {
    const registrationName = this.registrationName || this.translateService.instant(`${this.translateKey}SHARE.fallback`)

    return this.translateService.instant(`${this.translateKey}SHARE.label`, { registrationName })
  }

  public get orderDetailsText(): string {
    return this.translateService.instant(`${this.translateKey}ORDER.details`)
  }

  public copyLinkToClipboard(): void {
    if (!this.linkUrl) {
      return
    }

    if (this.clipboard.copy(this.linkUrl)) {
      this.seToast.done(
        this.translateWithFallback(`${this.translateKey}COPY_LINK.success`, this.copyLinkSuccessFallback)
      )
      return
    }

    this.seToast.error(
      this.translateWithFallback(`${this.translateKey}COPY_LINK.error`, this.copyLinkErrorFallback)
    )
  }

  private translateWithFallback(key: string, fallback: string): string {
    const translated = this.translateService.instant(key)

    return typeof translated === 'string' && translated !== key ? translated : fallback
  }
}
